import {
  CheckCircle2,
  Clock3,
  CloudUpload,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";

export type RecordingStatus =
  | "preparing"
  | "recording"
  | "uploading"
  | "finalizing"
  | "ready"
  | "failed";

export type RecordingStatusStripCopy = {
  preparingStatus: string;
  recordingStatus: string;
  uploadingStatus: string;
  finalizingStatus: string;
  readyStatus: string;
  failedStatus: string;
  elapsedLabel: string;
  pendingChunksLabel: string;
  acknowledgedChunkLabel: string;
  noAcknowledgedChunk: string;
  playbackLabel: string;
  retryAction: string;
};

export type RecordingStatusStripProps = {
  status: RecordingStatus;
  copy: RecordingStatusStripCopy;
  elapsedMs: number;
  pendingChunks: number;
  acknowledgedSequence: number | null;
  errorMessage?: string | null;
  playbackUrl?: string | null;
  playbackMimeType?: string;
  retryPending?: boolean;
  onRetry?(): void;
};

const STATUS_COPY_KEYS: Record<RecordingStatus, keyof RecordingStatusStripCopy> = {
  preparing: "preparingStatus",
  recording: "recordingStatus",
  uploading: "uploadingStatus",
  finalizing: "finalizingStatus",
  ready: "readyStatus",
  failed: "failedStatus",
};

export function RecordingStatusStrip({
  status,
  copy,
  elapsedMs,
  pendingChunks,
  acknowledgedSequence,
  errorMessage,
  playbackUrl,
  playbackMimeType,
  retryPending = false,
  onRetry,
}: RecordingStatusStripProps) {
  const elapsed = formatElapsedTime(elapsedMs);
  const statusClass = status === "recording"
    ? "listening"
    : status === "failed"
      ? "error"
      : status === "preparing" || status === "uploading" || status === "finalizing"
        ? "connecting"
        : "idle";

  return (
    <section
      className="recording-status-strip"
      data-recording-status={status}
      aria-label={copy[STATUS_COPY_KEYS[status]]}
    >
      <div className="recording-status-strip__summary">
        <span
          className={`status-badge status-badge--${statusClass}`}
          role="status"
          aria-live="polite"
        >
          {status === "recording" ? (
            <span className="status-badge__signal" aria-hidden="true" />
          ) : status === "ready" ? (
            <CheckCircle2 size={14} aria-hidden="true" />
          ) : status === "failed" ? (
            <TriangleAlert size={14} aria-hidden="true" />
          ) : (
            <CloudUpload size={14} aria-hidden="true" />
          )}
          {copy[STATUS_COPY_KEYS[status]]}
        </span>

        <dl className="recording-status-strip__metrics">
          <div>
            <dt>
              <Clock3 size={14} aria-hidden="true" />
              {copy.elapsedLabel}
            </dt>
            <dd>
              <time dateTime={toDuration(elapsedMs)}>{elapsed}</time>
            </dd>
          </div>
          <div>
            <dt>{copy.pendingChunksLabel}</dt>
            <dd><data value={pendingChunks}>{Math.max(0, pendingChunks)}</data></dd>
          </div>
          <div>
            <dt>{copy.acknowledgedChunkLabel}</dt>
            <dd>
              {acknowledgedSequence === null
                ? copy.noAcknowledgedChunk
                : <data value={acknowledgedSequence}>{acknowledgedSequence}</data>}
            </dd>
          </div>
        </dl>
      </div>

      {errorMessage && (
        <div className="recording-status-strip__error" role="alert">
          <TriangleAlert size={17} aria-hidden="true" />
          <p>{errorMessage}</p>
          {onRetry && (
            <button
              className="secondary-button secondary-button--compact"
              type="button"
              disabled={retryPending}
              aria-busy={retryPending}
              onClick={onRetry}
            >
              <RefreshCw size={15} aria-hidden="true" />
              {copy.retryAction}
            </button>
          )}
        </div>
      )}

      {status === "ready" && playbackUrl && (
        <div className="recording-status-strip__playback">
          <p>
            <CheckCircle2 size={16} aria-hidden="true" />
            {copy.playbackLabel}
          </p>
          <audio controls preload="metadata" aria-label={copy.playbackLabel}>
            <source src={playbackUrl} type={playbackMimeType} />
          </audio>
        </div>
      )}
    </section>
  );
}

function formatElapsedTime(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const minuteAndSecond = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${String(hours).padStart(2, "0")}:${minuteAndSecond}` : minuteAndSecond;
}

function toDuration(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1_000));
  return `PT${totalSeconds}S`;
}
